export interface MeteringItem {
  avgDailyUsage?: number;
  totalUsage: number;
  unitPrice?: string;
  usageFee: number;
}

export interface TicketMetering {
  ticket?: MeteringItem;
  ticketcount?: MeteringItem;
  mobileticket?: MeteringItem;
  monitoringticket?: MeteringItem;
}

export interface PhoneMetering {
  telticket?: MeteringItem;
  ibCallTime?: MeteringItem;
  obCallTime?: MeteringItem;
  scenario?: MeteringItem;
  mainnumber?: MeteringItem;
}

export interface ChatMetering {
  chat?: MeteringItem;
  kakao?: MeteringItem;
}

// addOn 항목은 정액 (usageFee만 옴), 사용량 없으면 null 응답
export interface AddOnMetering {
  knowledge?: MeteringItem | null;
  helpdoc?: MeteringItem | null;
  callback?: MeteringItem | null;
  electronicboard?: MeteringItem | null;
  smssend?: MeteringItem | null;
  endusermanagement?: MeteringItem | null;
  security?: MeteringItem | null;
}

export interface Metering {
  ticket?: TicketMetering;
  phone?: PhoneMetering;
  chat?: ChatMetering;
  addOn?: AddOnMetering;
}

export interface BillingUsagePeriod {
  startYmd: string;
  endYmd: string;
}

export interface PeriodMetering extends Metering {
  totalDays: number;
  billingUsagePeriodList: BillingUsagePeriod[];
  subTotalAmount: number;
}

export type ServiceBillingStatus = 'CREATE' | 'INUSE' | 'PAUSE' | 'STOP';

export interface BillingInfo {
  usageFee: number;
  discountFee: number;
  surchargeFee: number;
  finalFee: number;
}

// 서비스 레벨은 discountFee/surchargeFee가 항상 0이라 제외
export interface ServiceBillingInfo {
  usageFee: number;
  finalFee: number;
}

export interface ServiceBilling {
  serviceId: string;
  serviceName: string;
  serviceType: string;
  billingStatus: ServiceBillingStatus;
  billingInfo: ServiceBillingInfo;
  freetrialMetering: PeriodMetering;
  normalMetering: PeriodMetering;
  chargeExcludeMetering: PeriodMetering;
}

export interface OrganizationBilling {
  orgId: string;
  orgName: string;
  billingInfo: BillingInfo;
  services: ServiceBilling[];
}

export interface UsageSummaryContent {
  period: string;
  totalBillingInfo: BillingInfo;
  organizations: OrganizationBilling[];
}

// 결제 내역
export type PaymentStatus = 'COMPLETE' | 'FAIL';

export type SettlementMethod =
  | 'card'
  | 'banktrans'
  | 'payco'
  | 'japan'
  | 'foreigncard'
  | 'separaterequest';

export interface PaymentHistoryItem {
  paymentNo: string;
  paymentDate: string;
  itemName: string;
  billedAmount: number;
  paidAmount: number;
  paymentMethod: SettlementMethod;
  paymentStatus: PaymentStatus;
  paymentStatusName: string;
  resultMessage: string;
  showRepaymentButton: boolean;
}

export interface PaymentHistoryResult {
  contents: PaymentHistoryItem[];
  total: number;
  pages: number;
  pageNum: number;
  pageSize: number;
}

// 재결제
export interface RepaymentRequest {
  paymentNo: string;
}

export interface RepaymentContent {
  paymentNo: string;
  success: boolean;
}

export const RepaymentErrorCode = {
  REPAYMENT_PAYMENT_NOT_FOUND: 14010,
  REPAYMENT_NOT_OWNED_BY_MEMBER: 14011,
  REPAYMENT_NOT_ELIGIBLE: 14012,
} as const;

export type RepaymentErrorCode =
  (typeof RepaymentErrorCode)[keyof typeof RepaymentErrorCode];

// 영수증 조회
export type ExtraChargeType = 'discount' | 'additional';

export interface ExtraCharge {
  chargeType: ExtraChargeType;
  reason: string;
  chargeAmount: number;
}

export interface ReceiptContent {
  itemName: string;
  paymentMethod: SettlementMethod;
  paymentStatus: PaymentStatus;
  paymentStatusName: string;
  paymentDate: string;
  chargeName: string;
  chargedAmount: number;
  extraCharges: ExtraCharge[];
  subtotal: number;
  vat: number;
  /** 지난달 미납 이월액 (VAT 포함, 기획서 F. 미납 요금) — 미납 이월이 없으면 0 */
  previousUnpaidAmount: number;
  total: number;
  salesSlipUrl: string | null;
}

export const ReceiptErrorCode = {
  PAYMENT_NOT_FOUND: 14013,
  NOT_OWNED_BY_MEMBER: 14014,
  NOT_COMPLETED: 14015,
} as const;
export type ReceiptErrorCode =
  (typeof ReceiptErrorCode)[keyof typeof ReceiptErrorCode];
